import type {
  AirRequestPricingQuote,
  AirRequestSeatingPreference,
  AirRequestTripType,
} from '@/app/(main)/air-request/consts/air-request-const';
import type { AirRequestFileType, FlightAirportFormType } from '@/app/(main)/air-request/types';
import type { OptionType } from '@/app/components/form/MultiSearchInput/types';
import type { ResponseDataWithPagination, ResponseType } from './apiTypes';
import type { FrequentFlyerProgram } from './clientDetailsApiTypes';
import type { GenderType, Nullable } from './common';

export interface AirRequestData {
  id: number;
  identity: string;
  is_locked: boolean;
  airports: string;
  updated_at: string;
  locked_by: string | null;
}

export const TimeOfDay = {
  MORNING: 1,
  AFTERNOON: 2,
  EVENING: 3,
  NIGHT: 4,
} as const;

export type TimeOfDayType = (typeof TimeOfDay)[keyof typeof TimeOfDay];

export interface GetAirRequestsParams {
  status?: 1 | 2; // 1: Draft, 2: Submitted
  search?: string;
  per_page?: number;
  page?: number;
}

export interface DeleteAirRequestRequest {
  id: number;
}

export interface CreateAirRequestResponse {
  id: number;
  updated_at: string;
  agent_id: number;
  agent_email: string;
  agent_phone: string;
}

export interface AircraftData {
  id: number;
  name: string;
}

export interface AirlineData {
  id: number;
  name: string;
}

export interface TravelPreferenceField {
  key: string;
  value: string | null;
}

export interface CabinClassData {
  id: number;
  name: string;
}

export interface TripPurposeData {
  id: number;
  name: string;
}

export type TravelPreferenceAirType = {
  value: string | null;
  name?: string;
  id: number;
};

export type AirRequestDetailsData = {
  id: number;
  identity: string;
  status: number;
  price_quote_type: AirRequestPricingQuote;
  seating_preference: AirRequestSeatingPreference | null;
  same_itinerary: boolean;
  is_specific_flight: boolean;
  created_at: string;
  updated_at: string;
  agent_id: number;
  agent_name: string;
  agent_email: string;
  agent_phone: string;
  agency_name: string | null;
  trip_type: AirRequestTripType | null;
  flight_details: string | null;
  special_request: string | null;
  additional_trip_notes: string | null;
  fee_information?: string | null;
  is_international_flight: boolean;
  flight_files: AirRequestFileType[] | null;
  flights: FlightData[] | null;
  is_urgent?: boolean;
  created_by_user: {
    id: number;
    name: string;
    email: string;
  };
  updated_by_user: {
    id: number;
    name: string;
    email: string;
  };
  request_types: [
    {
      id: number;
      name: string;
    },
  ];
  aircraft?: AircraftData[] | null;
  airlines?: AirlineData[] | null;
  travel_preferences?: TravelPreferenceAirType[];
  is_locked: boolean;
};
export interface RelatedAgentData {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  image_relative_url: string;
}
export interface EditAirRequestData
  extends Omit<Partial<AirRequestDetailsData>, 'travel_preferences' | 'airlines' | 'aircraft'> {
  travel_preferences?: TravelPreferenceAirType[];
  airline_ids?: number[];
  aircraft_ids?: number[];
  agent_id?: number;
  price_quote_type?: AirRequestPricingQuote;
  flight_details?: string;
  special_request?: string | null;
  is_specific_flight?: boolean;
}

export interface ClientTravelerData {
  id: number;
  full_name: string;
  first_name: string;
  last_name: string;
}

export interface GetClientTravelersParams {
  clientId: string;
  air_request_id: number;
  search?: string;
  page?: number;
  per_page?: number;
}

export interface AddPassengersRequest {
  travelers: number[];
}

export type AddPassengersResponseData = {
  additional_trip_notes: string | null;
  cabin_classes: [];
  client_identity: string | null;
  dob: string | null;
  email: string | null;
  first_name: string;
  flight_details: string | null;
  flights: [];
  id: number;
  is_primary: null;
  is_specific_flight: false;
  gender: GenderType | null;
  last_name: string;
  middle_name: string | null;
  is_international_flight: boolean;
  frequent_flyers: FrequentFlyerProgram[];
  phone: string | null;
  passport_files: AirRequestFileType[];
  flight_files: AirRequestFileType[];
  trip_type: number;
  global_entry: string;
  global_expiry_date: string;
  tsa_pre_check_number?: string;
  known_traveler_number: string;
  ktn_expiry_date: string;
};

export type UpdatePassengerRequest = {
  first_name: string;
  middle_name: string;
  last_name: string;
  email: string;
  dob: string;
  phone: string;
  gender: GenderType | null;
  additional_trip_notes: string;
  is_international_flight: boolean;
  trip_type: AirRequestTripType;
  is_specific_flight: boolean;
  flight_details: string;
  cabin_class_ids: number[];
};

export type PassengerPatchBody = Partial<UpdatePassengerRequest> & {
  global_entry?: string | null;
  global_expiry_date?: string | null;
  known_traveler_number?: string | null;
  ktn_expiry_date?: string | null;
};

export type AddTravelerRequest = {
  first_name: string;
  middle_name?: Nullable<string>;
  last_name: string;
  email?: string;
  dob: string;
  gender: GenderType | null;
  phone?: string;
  is_related_passenger: boolean;
  is_group_client: boolean;
  relationship_type_id: number | null;
};

export interface GetRelationshipTypesData {
  id: number;
  name: string;
}

export interface GetPreferredCabinsData {
  id: number;
  name: string;
}

export interface CabinClassData {
  id: number;
  name: string;
}

export interface MultiPassengerRequest {
  additional_trip_notes: string;
  trip_type: number;
  is_specific_flight: boolean;
  flight_details: string;
  cabin_class_ids: number[];
}

export type FlightAirportType = (typeof FlightAirportTypes)[keyof typeof FlightAirportTypes];
export const FlightAirportTypes = {
  DEPARTURE: 1,
  ARRIVAL: 2,
} as const;

export interface SelectedFlightCityType {
  id: number;
  name: string;
  code: string;
  country_name: string;
  country_code: string;
  airport_count: number;
  airport_ids: number[];
}

export interface SelectedFlightAirportType {
  id: number;
  name: string;
  code: string;
  city_id: number;
}

export type FlightAirport = {
  cities: SelectedFlightCityType[];
  airports: SelectedFlightAirportType[];
};

export type UpdateFlightDateFormType = {
  departure_date: string | null;
  departure_date_end: string | null;
  is_flexible_date: boolean;
  flexible_date_description: string | null;
};
export interface UpdateFlightAirportRequest {
  airport_ids: number[];
  city_ids: number[];
}

export interface UpdateFlightRequest {
  departure_date: string | null;
  departure_date_end: string | null;
  departure_time_details: TimeOfDayType[];
  cabin_class_ids: number[];
  is_flexible_date: boolean;
  flexible_date_description: string | null;
  departure: UpdateFlightAirportRequest;
  arrival: UpdateFlightAirportRequest;
}

export interface FlightData {
  departure_date: null | string;
  departure_date_end: null | string;
  departure_time_details: TimeOfDayType[];
  is_flexible_date: boolean;
  cabin_classes?: CabinClassData[] | null;
  flexible_date_description: string | null;
  id: number;
  departure: FlightAirport;
  arrival: FlightAirport;
  passenger_id: number;
}

export interface FlightFormData {
  id: number;
  flight_id: number;
  departure_date: Date | null;
  departure_date_end: Date | null;
  departure_time_details: TimeOfDayType[];
  is_flexible_date: boolean;
  cabin_classes: OptionType<number, string>[];
  flexible_date_description: string | null;
  departure: FlightAirportFormType | null;
  arrival: FlightAirportFormType | null;
}

export interface CountryData {
  id: number;
  name: string;
  code: string;
}

export interface PassengerData {
  id: number;
  is_primary: boolean;
  client_identity?: string;
  first_name: string;
  middle_name?: string | null;
  is_specific_flight: boolean;
  trip_type: AirRequestTripType;
  special_request: string | null;
  gender: GenderType | null;
  flight_details?: string;
  additional_trip_notes?: string;
  is_international_flight: boolean;
  last_name: string;
  dob: string;
  phone?: string;
  email?: string;
  flights?: FlightData[];
  passport_files: AirRequestFileType[];
  flight_files: AirRequestFileType[];
  frequent_flyers?: FrequentFlyerProgram[];
  travel_preferences?: TravelPreferenceField[];
  global_entry?: string;
  tsa_pre_check_number?: string;
  known_traveler_number?: string;
}

export interface CreateFlightResponseData {
  id: number;
  passenger_id?: number;
  departure_date: string;
  departure_time_details: TimeOfDayType[];
}

export interface DeleteFlightRequest {
  id: number;
}

export interface DeletePassengerFileRequest {
  target_id: number;
}

export interface GetFrequentFlyersData {
  id: number;
  name: string;
}

export interface AddFrequentFlyerProgramRequest {
  frequent_flyer_id: number;
  program_number?: string;
}

export interface EditFrequentFlyerProgramRequest {
  frequent_flyer_id?: number;
  program_number: string;
}

export interface EditFrequentFlyerProgramResponse {
  data: {
    frequent_flyer_id: number;
    program_number: string;
  };
}

export interface AddFrequentFlyerProgramResponse {
  data: {
    frequent_flyer: string;
    id: number;
    frequent_flyer_id: number;
  };
}

export interface DeleteFrequentFlyerProgramRequest {
  id: number;
}

export interface GetAirportListCityData {
  id: number;
  name: string;
  code: string;
  country: string;
  country_code: string;
  airports_count: number;
}

export interface GetAirportListData {
  id: number;
  name: string;
  code: string;
  city: GetAirportListCityData;
}

export interface UpdateSameItineraryData {
  same_itinerary: boolean;
}

export interface UpdateSpecificFlightData {
  is_specific_flight: boolean;
}

export interface UpdateTripTypeData {
  trip_type: AirRequestTripType;
}

export type GetAirRequestsResponse = ResponseDataWithPagination<AirRequestData[]>;
export type GetClientTravelersResponse = ResponseDataWithPagination<ClientTravelerData[]>;
export type PostCreateAirRequestResponse = ResponseType<CreateAirRequestResponse>;
export type GetAirRequestDetailsResponse = ResponseType<AirRequestDetailsData>;
export type AddPassengersResponse = ResponseType<AddPassengersResponseData[]>;
export type GetRelationshipTypesResponse = ResponseDataWithPagination<GetRelationshipTypesData[]>;
export type GetRelatedAgentsResponse = ResponseDataWithPagination<RelatedAgentData[]>;
export type GetPreferredCabinsResponse = ResponseDataWithPagination<GetPreferredCabinsData[]>;
export type GetPassengersResponse = ResponseDataWithPagination<PassengerData[]>;
export type CreateFlightResponse = ResponseType<CreateFlightResponseData>;
export type GetAirportListResponse = ResponseDataWithPagination<GetAirportListData[]>;
